import React, { useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router';

import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import PasswordStrengthBar from 'app/shared/layout/password/password-strength-bar';
import { handlePasswordResetFinish, reset } from '../password-reset.reducer';

interface ResetFinishFormValues {
  newPassword: string;
  confirmPassword: string;
}

export const PasswordResetFinishPage = () => {
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ResetFinishFormValues>({ mode: 'onTouched' });

  // Drives the strength bar. `watch` re-renders on every keystroke, which is what the bar needs; the
  // confirmation rule reads `getValues` instead so it can never compare against a stale render.
  const newPassword = watch('newPassword', '');

  useEffect(
    () => () => {
      dispatch(reset());
    },
    [],
  );

  const handleValidSubmit = ({ newPassword: password }: ResetFinishFormValues) =>
    dispatch(handlePasswordResetFinish({ key: key!, newPassword: password }));

  const getResetForm = () => {
    return (
      <Form onSubmit={handleSubmit(handleValidSubmit)}>
        <Form.Group className="mb-3" controlId="newPassword">
          <Form.Label>Nueva contraseña</Form.Label>
          <Form.Control
            type="password"
            placeholder="Nueva contraseña"
            data-cy="resetPassword"
            isInvalid={!!errors.newPassword}
            {...register('newPassword', {
              required: { value: true, message: 'Se requiere que ingrese una contraseña.' },
              minLength: { value: 8, message: 'Se requiere que su contraseña tenga por lo menos 8 caracteres' },
              maxLength: { value: 50, message: 'Su contraseña no puede tener más de 50 caracteres' },
            })}
          />
          <Form.Control.Feedback type="invalid">{errors.newPassword?.message}</Form.Control.Feedback>
        </Form.Group>
        <PasswordStrengthBar password={newPassword} />
        <Form.Group className="mb-3" controlId="confirmPassword">
          <Form.Label>Confirmación de la nueva contraseña</Form.Label>
          <Form.Control
            type="password"
            placeholder="Confirmación de la nueva contraseña"
            data-cy="confirmResetPassword"
            isInvalid={!!errors.confirmPassword}
            {...register('confirmPassword', {
              required: { value: true, message: 'Se requiere que confirme la contraseña.' },
              minLength: { value: 8, message: 'Se requiere que su contraseña de confirmación tenga por lo menos 8 caracteres' },
              maxLength: { value: 50, message: 'Su contraseña de confirmación no puede tener más de 50 caracteres' },
              validate: v => v === getValues('newPassword') || '¡La contraseña y la confirmación de contraseña no coinciden!',
            })}
          />
          <Form.Control.Feedback type="invalid">{errors.confirmPassword?.message}</Form.Control.Feedback>
        </Form.Group>
        <Button variant="success" type="submit" data-cy="submit">
          Validar la nueva contraseña
        </Button>
      </Form>
    );
  };

  const successMessage = useAppSelector(state => state.passwordReset.successMessage);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
    }
  }, [successMessage]);

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="4">
          <h1>Restablecer la contraseña</h1>
          <div>{key && getResetForm()}</div>
        </Col>
      </Row>
    </div>
  );
};

export default PasswordResetFinishPage;
